import { create } from 'zustand';
import type { UnsubscribeFunc } from 'pocketbase';
import { pb, getFileToken } from '../lib/pb';
import { errorMessage } from '../lib/errors';
import { toast } from '../store/store';
import type {
  DataCategoryRecord,
  DataClassRecord,
  DataFieldRecord,
  DataIconRecord,
  DataImportRecord,
  DataRecord,
  DataSheetRecord,
} from './types';
import { settingKey } from './schema';
import type { FilterSelection } from './filters';
import type { SheetView } from './sheet';

// Game data is global (not per map) and loaded the first time the "Speldata" view opens.
// Realtime keeps it fresh; a reconnect refetches, since events are lost while offline.

interface DataState {
  loaded: boolean;
  loading: boolean;
  categories: DataCategoryRecord[];
  classes: Record<string, DataClassRecord>; // by className
  settings: Record<string, DataFieldRecord>; // by `${owner}|${name}`
  records: Record<string, DataRecord>; // by id
  icons: Record<string, DataIconRecord>; // by sprite key
  lastImport: DataImportRecord | null;
  category: string | null;
  selectedId: string | null;
  search: string;
  /** Chosen record filters per category (see filters.ts); they stay when you switch category and back. */
  filters: Record<string, FilterSelection>;
  /** The sheet view's own columns (difficulty, notes) by record id; web-only (data_sheet). */
  sheet: Record<string, DataSheetRecord>;
  /** Table or sheet, per category: a per-viewer choice kept in localStorage. */
  views: Record<string, CategoryView>;
  /** Sort, column filters and search of the sheet view per category, kept while a record is open. */
  sheetViews: Record<string, SheetView>;
}

export type CategoryView = 'table' | 'sheet';

const VIEWS_KEY = 'mapeditor.data.views';

function loadViews(): Record<string, CategoryView> {
  try {
    return JSON.parse(localStorage.getItem(VIEWS_KEY) ?? '{}') as Record<string, CategoryView>;
  } catch {
    return {};
  }
}

const initial: DataState = {
  loaded: false,
  loading: false,
  categories: [],
  classes: {},
  settings: {},
  records: {},
  icons: {},
  lastImport: null,
  category: null,
  selectedId: null,
  search: '',
  filters: {},
  sheet: {},
  views: {},
  sheetViews: {},
};

export const useData = create<DataState>(() => ({ ...initial, views: loadViews() }));
export const setData = useData.setState;
export const getData = useData.getState;

/** Choose a value for one filter of a category; '' means all. */
export function setFilter(category: string, field: string, value: string) {
  const all = getData().filters;
  setData({ filters: { ...all, [category]: { ...all[category], [field]: value } } });
}

export function clearFilters(category: string) {
  const filters = { ...getData().filters };
  delete filters[category];
  setData({ filters });
}

/** Show a category as the table (balancing view) or the sheet. */
export function setCategoryView(category: string, view: CategoryView) {
  const views = { ...getData().views, [category]: view };
  setData({ views });
  try {
    localStorage.setItem(VIEWS_KEY, JSON.stringify(views));
  } catch {
    /* private mode etc. — a per-viewer convenience only */
  }
}

export function setSheetView(category: string, view: SheetView) {
  setData({ sheetViews: { ...getData().sheetViews, [category]: view } });
}

let unsubs: UnsubscribeFunc[] = [];

async function fetchAll() {
  const [categories, classes, fields, records, icons, imports, sheet] = await Promise.all([
    pb.collection('data_categories').getFullList<DataCategoryRecord>({ sort: 'sortOrder,label' }),
    pb.collection('data_classes').getFullList<DataClassRecord>({ batch: 1000 }),
    pb.collection('data_fields').getFullList<DataFieldRecord>({ batch: 1000 }),
    pb.collection('data_records').getFullList<DataRecord>({ batch: 1000 }),
    pb.collection('data_icons').getFullList<DataIconRecord>({ batch: 1000 }),
    pb.collection('data_imports').getList<DataImportRecord>(1, 1, { sort: '-created' }),
    pb.collection('data_sheet').getFullList<DataSheetRecord>({ batch: 1000 }),
  ]);
  const s = getData();
  const visible = categories.filter((c) => !c.missing);
  setData({
    loaded: true,
    categories: visible,
    classes: Object.fromEntries(classes.map((c) => [c.className, c])),
    settings: Object.fromEntries(fields.map((f) => [settingKey(f.owner, f.name), f])),
    records: Object.fromEntries(records.map((r) => [r.id, r])),
    icons: Object.fromEntries(icons.map((i) => [i.key, i])),
    lastImport: imports.items[0] ?? null,
    sheet: Object.fromEntries(sheet.map((x) => [x.record, x])),
    category:
      s.category && (s.category === '__changes' || visible.some((c) => c.key === s.category))
        ? s.category
        : (visible[0]?.key ?? null),
  });
}

async function subscribe() {
  for (const u of unsubs) await u().catch(() => undefined);
  unsubs = [
    await pb.collection('data_records').subscribe<DataRecord>('*', (e) => {
      const records = { ...getData().records };
      if (e.action === 'delete') delete records[e.record.id];
      else records[e.record.id] = e.record;
      setData({ records });
    }),
    await pb.collection('data_fields').subscribe<DataFieldRecord>('*', (e) => {
      const settings = { ...getData().settings };
      const k = settingKey(e.record.owner, e.record.name);
      if (e.action === 'delete') delete settings[k];
      else settings[k] = e.record;
      setData({ settings });
    }),
    await pb.collection('data_sheet').subscribe<DataSheetRecord>('*', (e) => {
      const sheet = { ...getData().sheet };
      if (e.action === 'delete') delete sheet[e.record.record];
      else sheet[e.record.record] = e.record;
      setData({ sheet });
    }),
    await pb.realtime.subscribe('PB_CONNECT', () => {
      // Skip the connect that belongs to our own subscribe() call; later ones are reconnects.
      if (getData().loaded && !getData().loading)
        void fetchAll().catch((err) => toast(errorMessage(err), 'error'));
    }),
  ];
}

/** Loads everything (first open, or after an import). */
export async function loadGameData(force = false) {
  const s = getData();
  if (s.loading || (s.loaded && !force)) return;
  setData({ loading: true });
  try {
    await fetchAll();
    if (unsubs.length === 0) await subscribe();
  } catch (err) {
    toast(errorMessage(err), 'error');
  } finally {
    setData({ loading: false });
  }
}

/** On logout: forget everything so the next account starts clean. */
export async function resetGameData() {
  for (const u of unsubs) await u().catch(() => undefined);
  unsubs = [];
  setData({ ...initial, views: getData().views });
}

let tokenRequest: Promise<string> | null = null;
/** One shared request for the protected-file token (icons render dozens at once). */
export function iconToken(): Promise<string> {
  tokenRequest ??= getFileToken().finally(() => setTimeout(() => (tokenRequest = null), 60_000));
  return tokenRequest;
}

export function iconUrl(icon: DataIconRecord, token: string): string {
  return pb.files.getURL(icon, icon.file, { token });
}
